import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import api from '@/api';
import { isErrorCode } from '@/api/errors';
import { QuizContent } from '@/courses/quiz/QuizContent';
import { QuizResultPanel } from '@/courses/quiz/QuizResultPanel';
import { useCourseContext } from '~/contexts/courseContext';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute('/_app/_course/lessons/$lessonSlug_/quiz')(
  {
    component: LessonQuizScreen,
  },
);

function LessonQuizScreen() {
  const { lessonSlug } = Route.useParams();
  const { setSelectedLessonSlug, setExpandedLessons } = useCourseContext();
  const { me, isStudent } = useCurrentUser();
  const language = me?.language ?? 'EN';

  const { data: lesson } = api.lessonBySlug.useQuery({
    path: { slug: lessonSlug },
    query: { language },
  });

  const lessonId = lesson?.id;

  const { data: lessonDetail } = api.lessonDetail.useQuery(
    { path: { lesson_id: lessonId ?? 0 } },
    { enabled: lessonId != null },
  );

  const courseId = lessonDetail?.course_id;

  const { data: courseDetail } = api.courseDetail.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: viewerState } = api.courseViewerState.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  // The whole screen, from server state. Legacy re-renders this ROUTE after a
  // submit rather than rendering the submit response — it overrides
  // `wpProQuizFront.methode.showResults` to `window.location.reload()` on a pass
  // and injects the failure block on a fail — so `QuizSubmissionResult` cannot
  // drive it and the mutation's result is deliberately discarded below.
  const { data: result, error: resultError } = api.lessonQuizResult.useQuery(
    { path: { lesson_id: lessonId ?? 0 } },
    { enabled: lessonId != null },
  );

  const state = result?.state;
  // Two of the six states render the form, and FAILED renders it UNDER the
  // failure panel: `LessonQuiz.php:114-118` is the one arm of
  // `display_quiz_graded_status` that returns `$content . ob_get_clean()` rather
  // than the panel alone, so the retake affordance is the preserved form itself.
  const showsForm = state === 'not_taken' || state === 'failed';

  // Fetched only in the states that render the form. `lesson_questions` is now
  // quiz-locked, so a NO_ACCESS student 403s on it — and an unconditional fetch
  // plus the old `if (!questions) return null` guard rendered them a blank page.
  const { data: questions, error: questionsError } =
    api.lessonQuestions.useQuery(
      { path: { lesson_id: lessonId ?? 0 } },
      { enabled: lessonId != null && showsForm },
    );

  // FAILED can be quiz-LOCKED at the same time, and the two reads disagree by
  // design: `lesson_quiz_result` resolves the completed attempt BEFORE the topic
  // lock, so a student who failed and then gained a new published topic answers
  // FAILED (not NO_ACCESS), while `require_lesson_quiz_unlocked` has no
  // completed-attempt override and still 403s `lesson_questions`. The form then
  // never arrived and `(showsForm && !questions)` held the screen at `null`
  // forever — the same blank-page trap that was closed for NO_ACCESS, displaced
  // onto FAILED. Measured on wbs_production 2026-08-17: over the latest complete
  // attempt per `(user, lesson)` that still has an incomplete PUBLISHED topic —
  // PENDING 7, GRADED 3, FAILED 1 — only the FAILED one reaches this branch, and
  // any lesson that gains a topic after a failure joins it.
  //
  // The lock is NOT relaxed server-side: `LessonQuiz.php:20-33` refuses whenever
  // the topics are short of 100%, regardless of attempts, so suppressing the form
  // and leaving the panel is the parity answer.
  const quizLocked = isErrorCode(questionsError, 'lesson_quiz_locked');
  const rendersForm = showsForm && !quizLocked;

  const { mutateAsync: submitLessonQuiz } = api.submitLessonQuiz.useMutation();

  useEffect(() => {
    if (!lessonSlug) return;
    setSelectedLessonSlug(lessonSlug);
    setExpandedLessons((prev) =>
      prev.includes(lessonSlug) ? prev : [...prev, lessonSlug],
    );
  }, [lessonSlug, setSelectedLessonSlug, setExpandedLessons]);

  const handleSubmitQuiz = async (
    answers: Record<string, string | string[] | boolean[]>,
  ) => {
    if (lessonId == null) return;
    await submitLessonQuiz({
      path: { lesson_id: lessonId },
      body: { answers: answers as any },
    });
    // Stay on the route and re-read the state; every post-submit page measured on
    // staging has `name="startQuiz"` count 0, i.e. legacy re-renders here too.
    // Navigating to the course page (what this did before) skipped all four of
    // legacy's result panels.
    await api.lessonQuizResult.invalidateQueries();
    await api.courseProgress.invalidateQueries();
  };

  const handleRetake = () => {
    const submit = document.getElementById('saveAndContinue');
    submit?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    submit?.focus();
  };

  // Legacy 302s a no-access quiz GET to the parent course, same as the lesson and
  // topic screens (§7.7). `lesson_quiz_result` is the gated read here, so the 403
  // is the primary signal and the viewer-state read is the belt-and-braces half —
  // it also answers `has_access = true` unconditionally for a non-student, so
  // staff in preview mode are never redirected.
  const courseSlug = courseDetail?.slug;
  const denied =
    viewerState?.has_access === false ||
    isErrorCode(resultError, 'course_access_required');
  if (denied && courseSlug !== undefined) {
    return (
      <Navigate to="/courses/$courseSlug/" params={{ courseSlug }} replace />
    );
  }

  if (!lessonDetail || !result || (rendersForm && !questions)) {
    return null;
  }

  return (
    <QuizContent
      courseTitle={courseDetail?.title ?? ''}
      courseSlug={courseSlug ?? ''}
      lessonTitle={lessonDetail.title}
      lessonSlug={lessonSlug}
      quizTitle={lessonDetail.title}
      questions={rendersForm ? (questions ?? []) : []}
      showsForm={rendersForm}
      isStudent={isStudent}
      onSubmitQuiz={handleSubmitQuiz}
      panel={
        state === 'not_taken' ? null : (
          <QuizResultPanel
            result={result}
            memberId={me?.id?.toString()}
            // Retake scrolls to the form, so it is only offered when there IS
            // one — a FAILED-and-quiz-locked student has no form to return to.
            onRetake={rendersForm ? handleRetake : undefined}
          />
        )
      }
    />
  );
}
